import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MAX_ATTEMPTS, mergeKeyboardStates, type AckResult, type GuessResponse, type LetterState, type MatchRecord } from "@mk/shared";
import { Board } from "../components/Board.tsx";
import { ConfirmDialog } from "../components/ConfirmDialog.tsx";
import { DuelClock } from "../components/DuelClock.tsx";
import { Keyboard } from "../components/Keyboard.tsx";
import { ResultBoard } from "../components/ResultBoard.tsx";
import { ResultEffects } from "../components/ResultEffects.tsx";
import { ScreenHeader } from "../components/ScreenHeader.tsx";
import { Toast } from "../components/Toast.tsx";
import { useBoardInput } from "../features/useBoardInput.ts";
import { useRaceStore } from "../features/race/raceStore.ts";
import { errorMessage, messages } from "../i18n/index.ts";
import type { RegisterBackHandler } from "../lib/navigation.ts";
import { useSession } from "../lib/session.ts";
import { getSocket, serverNow } from "../lib/socket.ts";
import { teamMatchClock } from "../features/team/teamCountdown.ts";

interface Props { onBack: () => void; onExitConfirmed: () => void; registerBackHandler: RegisterBackHandler }

function recordText(record: MatchRecord | undefined): string {
  if (!record) return "—";
  const rate = record.played ? `${Math.round(100 * record.wins / record.played)}%` : "—";
  return messages.duel.record(record.played, record.wins, record.losses, record.draws, rate);
}

export function RaceScreen({ onBack, onExitConfirmed, registerBackHandler }: Props) {
  const state = useRaceStore((store) => store.state);
  const result = useRaceStore((store) => store.result);
  const userId = useSession((store) => store.user?.id);
  const [now, setNow] = useState(serverNow);
  const [notice, setNotice] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [exitBusy, setExitBusy] = useState(false);
  const [voted, setVoted] = useState(false);
  const sendingRef = useRef(false);
  const socket = getSocket();

  useEffect(() => {
    const id = window.setInterval(() => setNow(serverNow()), 200);
    return () => window.clearInterval(id);
  }, []);
  const clock = state ? teamMatchClock(state.startsAt, state.endsAt, now) : null;
  const inTransition = state?.transitionEndsAt !== null;

  const submit = useCallback((word: string | null) => {
    if (!state || sendingRef.current || !word) {
      if (!word) setNotice(messages.feedback.lengthRequired(5));
      return;
    }
    sendingRef.current = true;
    setSending(true);
    socket.timeout(8_000).emit("race:guess", { matchId: state.matchId, word,
      wordIndex: state.wordIndex, row: state.guesses.length },
    (timeout: Error | null, response: AckResult<GuessResponse>) => {
      sendingRef.current = false; setSending(false);
      setNotice(timeout ? messages.feedback.guessConnectionFailed : response.ok ? null : errorMessage(response));
    });
  }, [socket, state]);
  const input = useBoardInput({ disabled: !clock?.started || Boolean(inTransition) || sending || (clock?.remainingMs ?? 0) <= 0, onSubmit: submit });
  const clearDraft = input.clear;
  const identity = state ? `${state.matchId}:${state.wordIndex}:${state.guesses.length}` : "";
  useEffect(() => { clearDraft(); sendingRef.current = false; setSending(false); }, [identity, clearDraft]);
  useEffect(() => { setVoted(false); }, [state?.matchId]);

  const keyboard = useMemo(() => (state?.guesses ?? []).reduce<Record<string, LetterState>>(
    (keys, guess) => mergeKeyboardStates(keys, guess.word, guess.pattern), {}), [state?.guesses]);

  const leave = useCallback(() => {
    useRaceStore.getState().reset();
    onExitConfirmed();
  }, [onExitConfirmed]);
  const forfeit = useCallback(() => {
    if (!state || exitBusy) return;
    setExitBusy(true);
    socket.emit("race:forfeit", { matchId: state.matchId }, (response: AckResult<{ forfeited: true }>) => {
      setExitBusy(false);
      if (!response.ok) { setNotice(errorMessage(response)); setExitOpen(false); return; }
      leave();
    });
  }, [exitBusy, leave, socket, state]);

  useEffect(() => registerBackHandler(() => {
    if (exitOpen) { setExitOpen(false); return "block"; }
    if (result) { useRaceStore.getState().reset(); return "allow"; }
    setExitOpen(true); return "block";
  }), [exitOpen, registerBackHandler, result]);

  if (result) {
    const outcome = result.winnerId === null ? "draw" : result.winnerId === userId ? "win" : "loss";
    return <div className="app team-app">
      <ResultEffects matchId={result.matchId} outcome={outcome} />
      <ScreenHeader title={messages.race.resultTitle} onBack={leave} />
      <main className="overflow-y-auto px-4 pb-6"><div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
        <section className="team-result-hero" data-outcome={outcome}>
          <h1>{outcome === "win" ? messages.race.won : outcome === "draw" ? messages.race.draw : messages.race.lost}</h1>
          <div className="team-result-score"><b>{result.you.wordsSolved}</b><span>:</span><b>{result.rival.wordsSolved}</b></div>
          <p>{result.reason === "fewer-attempts" ? messages.race.fewerAttempts : result.reason === "forfeit" ? messages.team.forfeit : ""}</p>
        </section>
        <p className="text-sm text-muted">{messages.race.attempts(result.you.solvedAttempts, result.rival.solvedAttempts)}</p>
        {result.you.rounds.map((round) => <section key={round.wordIndex} className="team-round-result">
          <b>{round.wordIndex + 1}. {round.answer}</b><ResultBoard rows={round.guesses} />
        </section>)}
        <button type="button" className="btn-primary" disabled={voted} onClick={() => {
          if (voted) return;
          setVoted(true);
          socket.emit("race:rematch:vote", { matchId: result.matchId }, (response: AckResult<{ votes: number }>) => {
            if (!response.ok) { setVoted(false); setNotice(errorMessage(response)); }
          });
        }}>{voted ? messages.race.rematchWaiting(result.rematchVotes) : messages.race.rematch}</button>
        {notice && <p className="text-flame" role="alert">{notice}</p>}
        <button type="button" className="btn-secondary" onClick={leave}>{messages.duel.home}</button>
      </div></main>
    </div>;
  }

  if (!state || !clock) return <div className="app"><ScreenHeader title={messages.race.title} onBack={onBack} /></div>;
  return <div className="app team-app team-app--live">
    <ScreenHeader title={messages.race.title} onBack={() => setExitOpen(true)} />
    <div className="team-scorebar">
      <div><span>{messages.common.you}</span><b>{state.wordsSolved}</b></div>
      <DuelClock remainingMs={clock.remainingMs} canShorten={false} />
      <div><span>{state.opponent.username}</span><b>{state.opponentWordsSolved}</b></div>
    </div>
    <main className="team-live-main">
      <Toast message={notice} />
      {clock.countdownSecond !== null && <div className="vs-grid">
        <div className="plate-l vs-left"><div className="vs-name">{messages.common.you}</div><div className="vs-record">{recordText(state.records?.you)}</div></div>
        <span className="vs-mark">VS</span>
        <div className="plate-r vs-right"><div className="vs-name">{state.opponent.username}</div><div className="vs-record">{recordText(state.records?.opponent)}</div></div>
      </div>}
      <p className="text-sm text-muted">{messages.race.progress(state.wordIndex + 1, state.opponentWordIndex + 1)}</p>
      <div className="team-own-board">
        {clock.countdownSecond !== null && <div className="team-countdown-overlay" role="status"><span>{clock.countdownSecond}</span></div>}
        {inTransition ? <p className="team-transition">{messages.team.nextWord}</p> :
          <Board rows={state.guesses} cells={input.cells} pinned={input.pinned} onSelectCell={input.selectCell}
            shake={false} won={false} maxAttempts={MAX_ATTEMPTS} />}
      </div>
    </main>
    <Keyboard states={keyboard} onPress={input.press} disabled={!clock.started || Boolean(inTransition) || clock.remainingMs <= 0} busy={sending} />
    <ConfirmDialog open={exitOpen} title={messages.race.leaveTitle} message={messages.race.leaveMessage}
      confirmLabel={messages.team.leave} onConfirm={forfeit} onCancel={() => setExitOpen(false)} busy={exitBusy} confirmDelayMs={1200} />
  </div>;
}
